import { dietCalorieTotalOf, type DietLog } from "@couples/shared";
import { useMemo } from "react";

export interface DietCalorieSeries {
  userId: string;
  name: string;
  color: string;
}

interface Props {
  /** 古い順の日付(`YYYY-MM-DD`) */
  dates: string[];
  series: DietCalorieSeries[];
  logs: DietLog[];
}

const WIDTH = 320;
const HEIGHT = 136;
const PAD_L = 30;
const PAD_R = 6;
const PAD_T = 8;
const PAD_B = 16;

/** カロリーが無い日にも目盛りだけは出す既定の上限 */
const DEFAULT_TOP = 2000;

function xOf(index: number, count: number): number {
  if (count <= 1) return PAD_L;
  return PAD_L + (index / (count - 1)) * (WIDTH - PAD_L - PAD_R);
}

function shortDate(key: string): string {
  const [, month, day] = key.split("-");
  return month === undefined || day === undefined ? key : `${Number(month)}/${Number(day)}`;
}

/**
 * 二人の「その日の合計摂取カロリー」を同じ軸に重ねた折れ線。
 *
 * 体重の増減グラフ(DietTrendChart)と違い、絶対量をそのまま描く
 * (0kcal を基準に増減を語る意味が無いため)。入力していない日は点を打たず、
 * 線をその区間だけ切る(0kcal と「未入力」を混同しないため)。
 */
export default function DietCalorieChart({ dates, series, logs }: Props) {
  const calorieByKey = useMemo(() => {
    const map = new Map<string, number>();
    for (const log of logs) {
      const total = dietCalorieTotalOf(log);
      if (total !== null) map.set(`${log.authorId}:${log.loggedOn}`, total);
    }
    return map;
  }, [logs]);

  const valuesOf = (item: DietCalorieSeries) =>
    dates.map((date) => calorieByKey.get(`${item.userId}:${date}`) ?? null);

  const allValues = series.flatMap((item) =>
    valuesOf(item).filter((value): value is number => value !== null),
  );

  const bottom = 0;
  const top =
    allValues.length === 0 ? DEFAULT_TOP : Math.ceil(Math.max(...allValues, 200) / 200) * 200;

  const yOf = (value: number) =>
    PAD_T + ((top - value) / (top - bottom)) * (HEIGHT - PAD_T - PAD_B);

  // 目盛りは200kcal刻み。範囲が広いときは間引いて、線が潰れないようにする
  const step = Math.max(200, Math.ceil(top / 5 / 200) * 200);
  const ticks: number[] = [];
  for (let value = top; value >= bottom; value -= step) {
    ticks.push(value);
  }

  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];

  return (
    <svg
      viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
      className="w-full"
      role="img"
      aria-label="摂取カロリーの推移"
    >
      {ticks.map((value) => (
        <g key={value}>
          <line
            x1={PAD_L}
            x2={WIDTH - PAD_R}
            y1={yOf(value)}
            y2={yOf(value)}
            stroke="var(--color-line)"
            strokeWidth="1"
            strokeDasharray={value === bottom ? undefined : "3 3"}
          />
          <text x="0" y={yOf(value) + 3} fontSize="9" fill="var(--color-sub)">
            {value}
          </text>
        </g>
      ))}

      {series.map((item) => {
        const values = valuesOf(item);

        // 未入力の日をまたいで線をつながないよう、連続している区間ごとに分けて描く
        const segments: string[][] = [];
        let current: string[] = [];
        values.forEach((value, index) => {
          if (value === null) {
            if (current.length > 0) segments.push(current);
            current = [];
            return;
          }
          current.push(`${String(xOf(index, dates.length))},${String(yOf(value))}`);
        });
        if (current.length > 0) segments.push(current);

        let lastIndex = -1;
        values.forEach((value, index) => {
          if (value !== null) lastIndex = index;
        });
        const lastValue = lastIndex >= 0 ? values[lastIndex] : undefined;

        return (
          <g key={item.userId}>
            {segments.map((points, index) => (
              <polyline
                key={index}
                points={points.join(" ")}
                fill="none"
                stroke={item.color}
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}
            {lastValue !== undefined && lastValue !== null && (
              <circle
                cx={xOf(lastIndex, dates.length)}
                cy={yOf(lastValue)}
                r="3"
                fill={item.color}
              />
            )}
          </g>
        );
      })}

      {firstDate !== undefined && (
        <text x={PAD_L} y={HEIGHT - 3} fontSize="9" fill="var(--color-sub)">
          {shortDate(firstDate)}
        </text>
      )}
      {lastDate !== undefined && (
        <text
          x={WIDTH - PAD_R}
          y={HEIGHT - 3}
          fontSize="9"
          fill="var(--color-sub)"
          textAnchor="end"
        >
          {shortDate(lastDate)}
        </text>
      )}
    </svg>
  );
}
